import { InputAdornment, Typography } from '@material-ui/core';
import { Height } from '@material-ui/icons';
import { ToggleButton } from '@material-ui/lab';
import { FormikNumberField } from '@superdispatch/forms';
import { Column, Columns, Stack, useResponsiveValue } from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { useField, useFormikContext } from 'formik';
import { LengthIcon } from 'shared/icons';
import { SizesIcon } from 'shared/icons/Sizes';
import { WeightIcon } from 'shared/icons/WeightIcon';
import { useDriverPreferences } from 'shared/modules/driver/DriverPreferencesAPI';
import { DriverPreferencesVehicleDetailsDTO } from 'shared/modules/driver/DriverPreferencesDTO';
import { DriverPreferencesBanner } from '../DriverPreferencesBanner';
import { DriverPreferencesCheckboxList } from '../DriverPreferencesCheckboxList';
import { DriverPreferencesErrorMessage } from '../DriverPreferencesErrorMessage';
import { FormikToggleButtonGroup } from '../FormikToggleButtonGroup';
import { useVehicleTypeItems } from '../VehicleTypeItems';

interface VehicleDetailsSizesFieldItemProps {
  name: string;
  label: string;
  icon: React.ReactNode;
}

function VehicleDetailsSizesFieldItem({
  name,
  label,
  icon,
}: VehicleDetailsSizesFieldItemProps) {
  return (
    <FormikNumberField
      fullWidth={true}
      name={name}
      label={label}
      inputProps={{ decimalScale: 2, maxLength: 9 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">{icon}</InputAdornment>
        ),
      }}
    />
  );
}

export function VehicleDetailsStepContent() {
  const { errors } = useFormikContext<DriverPreferencesVehicleDetailsDTO>();

  const errorMessage = useField('preferred_vehicle_types')[1].error;

  const { data } = useDriverPreferences();

  const vehicleTypeItems = useVehicleTypeItems(data);

  const isMobile = useResponsiveValue(true, false);
  const isDriveaway = data?.transport_type === 'driveaway';

  return (
    <>
      <DriverPreferencesBanner text="Include vehicle preferences for better load matching." />
      <Box padding="small">
        <Stack space="small">
          {!isDriveaway && (
            <>
              <Stack space="small">
                <Columns align="center">
                  <Column>
                    <Typography>Can you move inoperable vehicles?</Typography>
                  </Column>
                  <Column width="fluid">
                    <FormikToggleButtonGroup
                      name="can_move_inoperable_vehicles"
                      displayError={false}
                    >
                      <ToggleButton value={true}>Yes</ToggleButton>
                      <ToggleButton value={false}>No</ToggleButton>
                    </FormikToggleButtonGroup>
                  </Column>
                </Columns>
                {errors.can_move_inoperable_vehicles && (
                  <DriverPreferencesErrorMessage
                    errorMessage={errors.can_move_inoperable_vehicles}
                  />
                )}
              </Stack>

              <Stack space="small">
                <Stack space={['xxsmall', 'none']}>
                  <TextBox variant="heading-4">
                    Maximum vehicle size per vehicle {isMobile && <br />}
                    (optional)
                  </TextBox>
                  <TextBox variant="caption" color="secondary">
                    Enter maximum vehicle size information based on your
                    trailer.
                  </TextBox>
                </Stack>

                <Stack space="medium">
                  <Columns space="medium">
                    <Column width="1/2">
                      <VehicleDetailsSizesFieldItem
                        name="vehicles_max_height_inch"
                        label="Height (″)"
                        icon={<Height fontSize="small" color="action" />}
                      />
                    </Column>

                    <Column width="1/2">
                      <VehicleDetailsSizesFieldItem
                        name="vehicles_max_length_inch"
                        label="Length (″)"
                        icon={<LengthIcon fontSize="small" color="action" />}
                      />
                    </Column>
                  </Columns>

                  <Columns space="medium">
                    <Column width="1/2">
                      <VehicleDetailsSizesFieldItem
                        name="vehicles_max_width_inch"
                        label="Width (″)"
                        icon={<SizesIcon fontSize="small" color="action" />}
                      />
                    </Column>

                    <Column width="1/2">
                      <VehicleDetailsSizesFieldItem
                        name="vehicles_max_weight_lbs"
                        label="Weight (lbs)"
                        icon={<WeightIcon fontSize="small" color="action" />}
                      />
                    </Column>
                  </Columns>
                </Stack>
              </Stack>
            </>
          )}

          <Box
            maxWidth="300px"
            paddingTop={isDriveaway ? 'xxsmall' : 'small'}
            paddingBottom="small"
          >
            <Stack space={['xxsmall', 'none']}>
              <TextBox variant="heading-4">Vehicle Type</TextBox>
              <TextBox variant="caption" color="secondary">
                Select the vehicle types you prefer to deliver.
              </TextBox>
            </Stack>
          </Box>

          <Box marginRight="-small">
            <DriverPreferencesCheckboxList
              items={vehicleTypeItems}
              groupName="preferred_vehicle_types"
              errorMessage={errorMessage || ''}
            />
          </Box>
        </Stack>
      </Box>
    </>
  );
}
